import { computed, ref, type Ref } from 'vue';
import { isAimetaBrowser } from '@/utils/platform';

/** 可选皮肤（已下线 pink / purple，读取到则回落为 tech） */
export type AimetaSkinType = 'white' | 'tech' | 'black' | 'emerald';

export type AimetaSelectableSkin = Exclude<AimetaSkinType, 'emerald'>;

export const AIMETA_SKIN_OPTIONS: Array<{
  value: AimetaSelectableSkin;
  color: string;
  labelKey: string;
}> = [
  { value: 'white', color: '#ffffff', labelKey: 'home.skin.white' },
  { value: 'tech', color: '#00a8d8', labelKey: 'home.skin.tech' },
  { value: 'black', color: '#161a21', labelKey: 'home.skin.black' },
];

const DEPRECATED_SKINS = new Set([
  'sunset',
  'crimson',
  'pink',
  'purple',
]);

const isValidSkin = (v: string | null): v is AimetaSkinType =>
  v === 'white' || v === 'tech' || v === 'black' || v === 'emerald';

const isSelectableSkin = (v: string | null): v is AimetaSelectableSkin =>
  v === 'white' || v === 'tech' || v === 'black';

/** 将存储/配置中的皮肤值规范为当前有效皮肤；pink/purple 等已下线主题 → tech */
export function normalizeAimetaSkin(
  v: string | null | undefined,
  fallback: AimetaSkinType = 'tech'
): AimetaSkinType {
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  if (!s) return fallback;
  if (DEPRECATED_SKINS.has(s)) return 'tech';
  return isValidSkin(s) ? s : fallback;
}

/** 浏览器始终可选肤；Win 壳等仅在 checkpoint skin=all 时可选 */
export function isAimetaSkinSelectable(configSkin?: string): boolean {
  if (isAimetaBrowser()) return true;
  return String(configSkin || '')
    .trim()
    .toLowerCase() === 'all';
}

export function applyAimetaSkin(val: string): void {
  const finalSkin = normalizeAimetaSkin(val, 'tech');
  document.documentElement.setAttribute('data-skin', finalSkin);
  localStorage.setItem('aimeta_skin', finalSkin);
}

/**
 * 根据 checkpoint/skins.ini 的 skin 与 localStorage 解析应应用的主题。
 * - skin=all（或浏览器）：优先 localStorage（主页选肤后直播间/知识库子 WebView 须一致）
 * - 固定 white/tech/black：用配置值，不把 all 误规范成 white
 */
export function resolveAppliedAimetaSkin(configSkin?: string): AimetaSkinType {
  const raw = String(configSkin ?? '')
    .trim()
    .toLowerCase();
  const userCanPick = isAimetaSkinSelectable(configSkin);

  if (userCanPick) {
    const stored = localStorage.getItem('aimeta_skin');
    if (stored) {
      const fromLocal = normalizeAimetaSkin(stored, 'tech');
      if (isValidSkin(fromLocal)) return fromLocal;
    }
    return 'tech';
  }

  if (isValidSkin(raw)) {
    return normalizeAimetaSkin(raw, 'white');
  }
  return 'white';
}

let skinStorageListenerInstalled = false;

/** 其它 WebView 改肤时同步（同进程多窗：主页改肤 → 已打开的直播间/知识库） */
export function installAimetaSkinStorageSync(): void {
  if (skinStorageListenerInstalled || typeof window === 'undefined') return;
  skinStorageListenerInstalled = true;
  window.addEventListener('storage', (e) => {
    if (e.key !== 'aimeta_skin' || e.newValue == null) return;
    applyAimetaSkin(e.newValue);
  });
}

export function syncAimetaSkinFromDocument(): AimetaSkinType {
  const rawCurrent = document.documentElement.getAttribute('data-skin');
  const current = normalizeAimetaSkin(rawCurrent, 'white');
  if (
    rawCurrent &&
    normalizeAimetaSkin(rawCurrent, 'tech') !== rawCurrent.trim().toLowerCase()
  ) {
    applyAimetaSkin(current);
  }
  return current;
}

/** 顶栏/设置页皮肤选择器 */
export function useAimetaSkin(
  configSkin?: Ref<string | undefined> | (() => string | undefined)
) {
  const skinSelectable = computed(() => {
    const skin =
      typeof configSkin === 'function'
        ? configSkin()
        : configSkin?.value;
    return isAimetaSkinSelectable(skin);
  });

  const selectedSkin = ref<AimetaSkinType>('white');

  const onSkinChange = (val: AimetaSkinType | AimetaSelectableSkin) => {
    const finalSkin = normalizeAimetaSkin(val, 'tech');
    selectedSkin.value = finalSkin;
    applyAimetaSkin(finalSkin);
  };

  const initSelectedSkin = () => {
    const current = syncAimetaSkinFromDocument();
    const normalized = normalizeAimetaSkin(current, 'tech');
    selectedSkin.value = normalized;
    if (!isSelectableSkin(normalized) && skinSelectable.value) {
      applyAimetaSkin('tech');
      selectedSkin.value = 'tech';
    } else if (normalized !== current) {
      applyAimetaSkin(normalized);
    }
  };

  return {
    skinSelectable,
    skinOptions: AIMETA_SKIN_OPTIONS,
    selectedSkin,
    onSkinChange,
    initSelectedSkin,
  };
}
